<template>
  <div class="video-reference-list-C">
    <div
      style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;"
    >
      <h4 style="margin: 0; color: #336; font-weight: bold;">
        Video Referansları
      </h4>
      <r-btn
        v-if="showAddButton"
        bgcolor="#15880d"
        @click="openAddModal"
        :disabled="isLoading"
      >
        Yeni Video Ekle
      </r-btn>
    </div>

    <div
      v-if="isLoading && videoReferences.length === 0"
      style="padding: 20px; text-align: center; color: #666;"
    >
      Yükleniyor...
    </div>

    <div
      v-else-if="videoReferences.length === 0"
      style="padding: 20px; text-align: center; color: #999; background: #f5f5f5; border-radius: 4px;"
    >
      Henüz video referansı eklenmemiş.
    </div>

    <r-data-list
      v-else
      :headdata="headdata"
      :listdata="videoReferences"
      :opt="opts"
    >
      <template v-slot:rowextrabtn="{ datarow }">
        <r-btn @click="openEditModal(datarow)" bgcolor="#ffaa11">Düzenle</r-btn>
        <r-btn @click="openDeleteModal(datarow)" bgcolor="#bb0202">Sil</r-btn>
      </template>
    </r-data-list>

    <!-- Video Ekleme/Düzenleme Modal -->
    <video-reference-modal
      v-model:isactive="showModal"
      :video-reference="selectedVideo"
      :question-id="questionId"
      :is-edit-mode="isEditMode"
      @saved="handleSaved"
    />

    <!-- Silme Onay Modal -->
    <r-modal
      v-model:isactive="showDeleteModal"
      title="Video Referansını Sil"
      :havecontent="true"
    >
      <div style="padding: 16px;">
        <p style="margin-bottom: 12px;">
          <strong>{{ selectedVideo?.videoName }}</strong> adlı video referansını
          silmek istediğinize emin misiniz?
        </p>
        <p
          v-if="selectedVideo?.sectionName"
          style="color: #666; font-size: 14px;"
        >
          Bölüm: {{ selectedVideo.sectionName }}
        </p>
      </div>
      <template v-slot:otherbuttons>
        <r-btn
          bgcolor="#bb0202"
          @click="confirmDelete"
          size="medium"
          :disabled="isDeleting"
        >
          {{ isDeleting ? "Siliniyor..." : "Sil" }}
        </r-btn>
      </template>
    </r-modal>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { useStore } from "vuex";
import VideoReferenceModal from "./VideoReferenceModal.vue";

export default {
  name: "VideoReferenceList",
  components: {
    VideoReferenceModal
  },
  props: {
    questionId: {
      type: Number,
      required: true
    },
    showAddButton: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { expose }) {
    const store = useStore();
    const videoReferences = ref([]);
    const isLoading = ref(false);
    const showModal = ref(false);
    const showDeleteModal = ref(false);
    const selectedVideo = ref(null);
    const isEditMode = ref(false);
    const isDeleting = ref(false);

    const headdata = computed(() => [
      { title: "Video Adı", key: "videoName", style: { width: "22%", minWidth: "90px" } },
      { title: "Bölüm", key: "sectionName", style: { width: "18%", minWidth: "80px" } },
      { title: "Başlangıç", key: "startTime", style: { width: "14%", minWidth: "85px" } },
      { title: "Bitiş", key: "endTime", style: { width: "14%", minWidth: "85px" } },
      { title: "Açıklama", key: "description", style: { width: "22%", minWidth: "100px" } }
    ]);

    const opts = computed(() => ({
      title: "Video Referansları",
      buttons: {
        style: { width: "20%", minWidth: "120px" }
      }
    }));

    const getVideoReferences = async () => {
      if (!props.questionId || props.questionId <= 0) {
        videoReferences.value = [];
        return;
      }

      try {
        isLoading.value = true;
        const res = await store.dispatch("getDatas", {
          api: `VideoReferences/question/${props.questionId}`
        });

        if (res && res.result) {
          const arr = res.data ?? res.Data;
          videoReferences.value = Array.isArray(arr) ? arr : [];
        } else {
          videoReferences.value = [];
        }
      } catch (error) {
        videoReferences.value = [];
      } finally {
        isLoading.value = false;
      }
    };

    const openAddModal = () => {
      selectedVideo.value = null;
      isEditMode.value = false;
      showModal.value = true;
    };

    const openEditModal = video => {
      selectedVideo.value = { ...video };
      isEditMode.value = true;
      showModal.value = true;
    };

    const openDeleteModal = video => {
      selectedVideo.value = { ...video };
      showDeleteModal.value = true;
    };

    const confirmDelete = async () => {
      if (isDeleting.value) return;
      if (!selectedVideo.value?.id) return;

      try {
        isDeleting.value = true;
        const res = await store.dispatch("deleteDatas", {
          api: `VideoReferences/${selectedVideo.value.id}`
        });

        if (res && res.result) {
          showDeleteModal.value = false;
          selectedVideo.value = null;
          await getVideoReferences();
        } else {
          alert("Video referansı silinirken bir hata oluştu.");
        }
      } catch (error) {
        alert("Video referansı silinirken bir hata oluştu.");
      } finally {
        isDeleting.value = false;
      }
    };

    const handleSaved = () => {
      showModal.value = false;
      selectedVideo.value = null;
      getVideoReferences();
    };

    // QuestionId değiştiğinde videoları yeniden yükle
    watch(
      () => props.questionId,
      (newId, oldId) => {
        if (newId && newId > 0 && newId !== oldId) {
          getVideoReferences();
        }
      },
      { immediate: false }
    );

    onMounted(() => {
      // Sadece questionId varsa ve geçerliyse yükle
      if (props.questionId && props.questionId > 0) {
        // Kısa bir gecikme ile mount işleminin tamamlanmasını bekle
        setTimeout(() => {
          getVideoReferences();
        }, 100);
      }
    });

    // Parent component'ten erişilebilmesi için expose et
    expose({
      openAddModal,
      getVideoReferences
    });

    return {
      videoReferences,
      isLoading,
      showModal,
      showDeleteModal,
      selectedVideo,
      isEditMode,
      isDeleting,
      headdata,
      opts,
      openAddModal,
      openEditModal,
      openDeleteModal,
      confirmDelete,
      handleSaved
    };
  }
};
</script>

<style scoped>
.video-reference-list-C {
  width: 100%;
}
</style>
